import { roundQuantity } from '../schedule/unit-of-measure';
import type { MeasureBy } from './geometry';
import { ownFactorsByKey } from './own-factor';
import {
  isPositioned,
  pileLength,
  pileRateKey,
  pileVolume,
  type PieceMark,
  type PileType,
  type StructuralPiece,
} from './pile';
import { STEEL_GAUGES, type SteelGauge } from './steel';

/**
 * O que a conta tira das estacas desenhadas (Fase 27, etapa 1b — D1, D4, D12, RN-364, RN-365).
 *
 * É a metade do `ShaftTotals` para a estaca: contagem, volume, comprimento, aço por bitola e as
 * marcadas de cada serviço. Mora aqui, e não em `pile.ts`, porque aquele arquivo é o contrato da
 * peça (tipo, regras de nome e de comprimento) e este é a conta que o desenho faz com ela.
 *
 * > **As regras em uma linha:** só a estaca **posicionada** conta (RN-364); a sem tipo
 * > conhecido é ignorada, porque o tipo dá o diâmetro, o comprimento padrão e o aço (RN-362);
 * > e a marca só vale para a estaca que conta (RN-365).
 */

/**
 * Três casas, como a coluna do tipo (`kg por estaca` e `comprimento`): o kg de aço de 43
 * estacas é exato com elas, e duas casas comeriam o grama.
 */
export const PILE_DECIMALS = 3;

export interface PileTotals {
  /** Quantas estacas — só as posicionadas, e de tipo conhecido (un). */
  readonly count: number;
  /** π·d²/4 × comprimento de cada uma, somado (m³). */
  readonly volume: number;
  /** O comprimento que vale de cada uma — o corrigido, quando há —, somado (m). */
  readonly length: number;
  /**
   * Os kg de aço de cada bitola: a soma do aço do **tipo** de cada estaca (RN-367).
   *
   * Por bitola porque a proporção muda de um tipo para o outro, e é ela que reparte o item de
   * aço do corte e dobra (`pileGaugeShares`). Só as bitolas que algum tipo usa têm chave.
   */
  readonly steelByGauge: Readonly<Partial<Record<SteelGauge, number>>>;
  /**
   * Quantas estacas estão marcadas para cada serviço, pela chave dele no catálogo (D4, RN-365).
   *
   * Só o serviço que tem marca aparece; o que não tem é zero, e quem lê decide (`?? 0`).
   */
  readonly marked: Readonly<Record<string, number>>;
  /**
   * Os mesmos números, tipo a tipo, pelo id do tipo — só os tipos que têm estaca contada
   * (Fase 39, RN-508). É o que reparte a medida do serviço entre os diâmetros, para cada um levar
   * o fator dele. Ausente nos totais montados à mão, de antes da Fase 39.
   */
  readonly byType?: Readonly<Record<string, PileTypeTotals>>;
  /**
   * Os mesmos números de cada estaca que tem fator próprio, pelo id dela (Fase 39, etapa 3,
   * RN-511). Só elas: a estaca sem fator próprio segue o tipo, e a conta não precisa vê-la
   * sozinha. Ausente quando nenhuma estaca contada tem fator próprio.
   */
  readonly byPiece?: Readonly<Record<string, PilePieceTotals>>;
}

/** Os números de contagem, volume, comprimento, aço e marcas — sem o tipo a tipo nem a peça. */
export type PileCounts = Omit<PileTotals, 'byType' | 'byPiece'>;

/** Os totais das estacas de **um** tipo, com a composição dele (Fase 39). */
export interface PileTypeTotals extends PileCounts {
  /** O fator de cada item, por `pileRateKey` — a composição do tipo (RN-504). */
  readonly rates: Readonly<Record<string, number>>;
}

/**
 * Os números de **uma** estaca com fator próprio (Fase 39, RN-511): a medida dela, o tipo de onde
 * a medida sai, e o fator próprio de cada item, por `ownFactorKey`.
 */
export interface PilePieceTotals extends PileCounts {
  readonly pileTypeId: string;
  readonly factors: Readonly<Record<string, number>>;
}

/** Sem estaca desenhada — o ponto de partida, e o que a tipologia sem fundação devolve. */
export const NO_PILES: PileTotals = {
  count: 0,
  volume: 0,
  length: 0,
  steelByGauge: {},
  marked: {},
};

/**
 * A conta das estacas de uma tipologia.
 *
 * Recebe as peças **da tipologia** — a estaca é da área comum do desenho do pavimento (RN-361),
 * e quem chama já separou as dela. A estaca sem centro está na fila do "faltam N" e não entra
 * (RN-364); a de tipo que não está na lista (excluído, ou de outra obra) também não: sem o tipo
 * não há diâmetro, e medir com um diâmetro adivinhado é número que parece certo.
 *
 * Marca repetida para a mesma estaca e o mesmo serviço conta uma vez; marca de estaca que não
 * conta (a fila, o tipo desconhecido, outra tipologia) não conta também.
 */
export function pileTotals(
  pieces: readonly StructuralPiece[],
  types: readonly Pick<PileType, 'id' | 'diameter' | 'length' | 'steel' | 'deletedAt' | 'rates'>[],
  marks: readonly PieceMark[],
): PileTotals {
  const tipos = new Map(
    types.filter((tipo) => tipo.deletedAt === undefined).map((tipo) => [tipo.id, tipo]),
  );
  const total = somar(pieces, tipos, marks);

  // Tipo a tipo (Fase 39, RN-508): a mesma conta, só com as estacas dele, e a composição junto.
  const byType: Record<string, PileTypeTotals> = {};
  for (const tipo of tipos.values()) {
    const doTipo = somar(
      pieces.filter((peca) => peca.kind === 'ESTACA' && peca.pileTypeId === tipo.id),
      tipos,
      marks,
    );
    if (doTipo.count === 0) continue;
    byType[tipo.id] = {
      ...doTipo,
      rates: Object.fromEntries(
        (tipo.rates ?? []).map((linha) => [
          pileRateKey(linha.serviceKey, linha.itemKey),
          linha.rate,
        ]),
      ),
    };
  }

  // Peça a peça, só a que tem fator próprio (Fase 39, etapa 3, RN-511): a medida dela, para a
  // conta tirá-la do tipo e multiplicá-la pelo fator dela. A que não conta (a fila, o tipo
  // desconhecido) não entra — `somar` a deixa com contagem zero.
  const byPiece: Record<string, PilePieceTotals> = {};
  for (const peca of pieces) {
    if (peca.kind !== 'ESTACA' || (peca.factors ?? []).length === 0) continue;
    const daPeca = somar([peca], tipos, marks);
    if (daPeca.count === 0) continue;
    byPiece[peca.id] = {
      ...daPeca,
      pileTypeId: peca.pileTypeId,
      factors: ownFactorsByKey(peca.factors),
    };
  }

  return {
    ...total,
    ...(Object.keys(byType).length === 0 ? {} : { byType }),
    ...(Object.keys(byPiece).length === 0 ? {} : { byPiece }),
  };
}

function somar(
  pieces: readonly StructuralPiece[],
  tipos: ReadonlyMap<string, Pick<PileType, 'diameter' | 'length' | 'steel'>>,
  marks: readonly PieceMark[],
): PileCounts {
  let count = 0;
  let volume = 0;
  let length = 0;
  const aco = new Map<SteelGauge, number>();
  const contadas = new Set<string>();

  for (const peca of pieces) {
    if (peca.kind !== 'ESTACA' || !isPositioned(peca)) continue;
    const tipo = tipos.get(peca.pileTypeId);
    if (tipo === undefined) continue;

    count += 1;
    volume += pileVolume(peca, tipo);
    length += pileLength(peca, tipo);
    for (const linha of tipo.steel) {
      aco.set(linha.gauge, (aco.get(linha.gauge) ?? 0) + linha.kgPerPile);
    }
    contadas.add(peca.id);
  }

  const marked: Record<string, number> = {};
  const vistas = new Set<string>();
  for (const marca of marks) {
    const chave = `${marca.pieceId}|${marca.serviceKey}`;
    if (!contadas.has(marca.pieceId) || vistas.has(chave)) continue;
    vistas.add(chave);
    marked[marca.serviceKey] = (marked[marca.serviceKey] ?? 0) + 1;
  }

  const steelByGauge: Partial<Record<SteelGauge, number>> = {};
  for (const bitola of STEEL_GAUGES) {
    const kg = aco.get(bitola.code);
    if (kg !== undefined) steelByGauge[bitola.code] = roundQuantity(kg, PILE_DECIMALS);
  }

  return {
    count,
    volume: roundQuantity(volume, PILE_DECIMALS),
    length: roundQuantity(length, PILE_DECIMALS),
    steelByGauge,
    marked,
  };
}

/** Os kg de aço de todas as estacas, todas as bitolas somadas. */
export function pileSteelTotal(totals: Pick<PileTotals, 'steelByGauge'>): number {
  return roundQuantity(
    Object.values(totals.steelByGauge).reduce((soma, kg) => soma + kg, 0),
    PILE_DECIMALS,
  );
}

/**
 * Quanto do aço das estacas é de cada bitola — a fração que reparte o medido (RN-378).
 *
 * É a `gaugeShares` da taxa da disciplina, só que o número vem do tipo de cada estaca desenhada
 * (RN-367): numa fundação com R40 e R60 misturadas, a fração de 6,3 mm é a dos dois tipos
 * somados, e não a de um deles. Vazio quando nenhuma estaca conta ou o tipo não tem aço — e aí
 * o item de aço não calcula.
 */
export function pileGaugeShares(
  totals: Pick<PileTotals, 'steelByGauge'>,
): Readonly<Partial<Record<SteelGauge, number>>> {
  const total = Object.values(totals.steelByGauge).reduce((soma, kg) => soma + kg, 0);
  if (total <= 0) return {};
  const fracoes: Partial<Record<SteelGauge, number>> = {};
  for (const bitola of STEEL_GAUGES) {
    const kg = totals.steelByGauge[bitola.code];
    if (kg !== undefined) fracoes[bitola.code] = kg / total;
  }
  return fracoes;
}

/**
 * Os totais multiplicados pelos andares que repetem o desenho — a estaca de cada um conta de
 * novo, como o shaft (RN-167). Cada número volta à casa dele; a contagem é inteira.
 */
export function scalePileTotals(totals: PileTotals, times: number): PileTotals {
  if (times === 1) return totals;
  const escala = (valor: number): number => roundQuantity(valor * times, PILE_DECIMALS);
  const escalar = <T extends PileCounts>(de: T): T => ({
    ...de,
    count: de.count * times,
    volume: escala(de.volume),
    length: escala(de.length),
    steelByGauge: Object.fromEntries(
      Object.entries(de.steelByGauge).map(([bitola, kg]) => [bitola, escala(kg)]),
    ),
    marked: Object.fromEntries(
      Object.entries(de.marked).map(([servico, quantas]) => [servico, quantas * times]),
    ),
  });
  return {
    ...escalar({
      count: totals.count,
      volume: totals.volume,
      length: totals.length,
      steelByGauge: totals.steelByGauge,
      marked: totals.marked,
    }),
    // A composição não escala: o fator é por unidade, e é a medida de cada tipo que cresce.
    ...(totals.byType === undefined
      ? {}
      : {
          byType: Object.fromEntries(
            Object.entries(totals.byType).map(([tipo, doTipo]) => [tipo, escalar(doTipo)]),
          ),
        }),
    // O fator próprio também não escala (RN-511): é a medida da estaca que se repete.
    ...(totals.byPiece === undefined
      ? {}
      : {
          byPiece: Object.fromEntries(
            Object.entries(totals.byPiece).map(([peca, daPeca]) => [peca, escalar(daPeca)]),
          ),
        }),
  };
}

/**
 * A medida de um serviço da estaca nuns totais — os da tipologia ou os de um tipo (Fase 39).
 *
 * É a leitura da estaca do desenho (`LEITURA_DO_PAR.ESTACA`, em `geometry.ts`): o aço dos tipos,
 * o volume, o comprimento, a contagem e as marcadas **deste** serviço. Fica aqui para a conta
 * repartir a medida entre os tipos sem refazer o desenho; o teste cobra que as duas leituras dão o
 * mesmo número.
 */
export function pileMeasure(
  measureBy: MeasureBy | undefined,
  totals: PileCounts,
  serviceKey?: string,
): number | undefined {
  if (measureBy === 'ACO') return pileSteelTotal(totals);
  if (measureBy === 'VOLUME') return totals.volume;
  if (measureBy === 'LENGTH') return totals.length;
  if (measureBy === 'COUNT') return totals.count;
  if (measureBy === 'MARKED') {
    return serviceKey === undefined ? undefined : (totals.marked[serviceKey] ?? 0);
  }
  return undefined;
}

/**
 * Quanto da medida de um serviço da estaca é de cada tipo — a fração que leva o fator dele
 * (Fase 39, RN-508).
 *
 * Como a participação das bitolas (`pileGaugeShares`): a medida do serviço continua a da
 * tipologia — corrigida, quando alguém corrigiu —, e a fração diz quanto dela é R40 e quanto é
 * R80. Vazio quando nenhum tipo mede: aí o item calcula como antes, pelo índice da obra.
 */
export function pileTypeShares(
  measureBy: MeasureBy | undefined,
  totals: Pick<PileTotals, 'byType'> | undefined,
  serviceKey?: string,
): Readonly<Record<string, number>> {
  const { porTipo, total } = medidaPorTipo(measureBy, totals, serviceKey);
  if (total <= 0) return {};
  return Object.fromEntries(porTipo.map(([tipo, medida]) => [tipo, medida / total]));
}

/** A medida de cada tipo que mede, e a soma delas — o denominador das partes. */
function medidaPorTipo(
  measureBy: MeasureBy | undefined,
  totals: Pick<PileTotals, 'byType'> | undefined,
  serviceKey: string | undefined,
): { readonly porTipo: readonly (readonly [string, number])[]; readonly total: number } {
  const porTipo = Object.entries(totals?.byType ?? {}).flatMap(([tipo, doTipo]) => {
    const medida = pileMeasure(measureBy, doTipo, serviceKey);
    return medida === undefined || medida <= 0 ? [] : [[tipo, medida] as const];
  });
  return { porTipo, total: porTipo.reduce((soma, [, medida]) => soma + medida, 0) };
}

/**
 * Quanto da medida de um serviço da estaca é de cada estaca com fator próprio (Fase 39, etapa 3,
 * RN-511), pelo id dela.
 *
 * Sobre o **mesmo** total de `pileTypeShares` — a soma das medidas dos tipos —, para a conta tirar
 * a parte da estaca da parte do tipo dela: o que sobra do tipo leva o fator do tipo, e a estaca
 * leva o dela. Com a medida corrigida na tipologia, a estaca leva a mesma proporção. Vazio quando
 * nenhuma estaca tem fator próprio, ou nenhuma mede neste serviço (a não marcada, no `MARKED`).
 */
export function pilePieceShares(
  measureBy: MeasureBy | undefined,
  totals: Pick<PileTotals, 'byType' | 'byPiece'> | undefined,
  serviceKey?: string,
): Readonly<Record<string, number>> {
  if (totals?.byPiece === undefined) return {};
  const { total } = medidaPorTipo(measureBy, totals, serviceKey);
  if (total <= 0) return {};
  const partes: Record<string, number> = {};
  for (const [peca, daPeca] of Object.entries(totals.byPiece)) {
    const medida = pileMeasure(measureBy, daPeca, serviceKey);
    if (medida !== undefined && medida > 0) partes[peca] = medida / total;
  }
  return partes;
}
